import { Table } from '@tanstack/react-table';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';

/**
 * DataTablePagination Component
 *
 * A pagination component for navigating and controlling the page size of a data table.
 * It integrates with the `@tanstack/react-table` pagination API to allow users to
 * navigate between pages and select the number of rows per page.
 *
 * Features:
 * - Displays the current page and total number of pages.
 * - Allows selecting the number of rows per page from predefined options.
 * - Includes navigation buttons to move to the first, previous, next, and last pages.
 * - Optionally displays the number of selected rows in the table.
 *
 * @template TData - The type of data used in the table.
 *
 * @param {Table<TData>} table - The instance of the table being paginated, from `@tanstack/react-table`.
 * @param {(pagination: { pageIndex: number; pageSize: number }) => void} [onPaginationChange] - Optional callback to handle pagination changes (e.g., when the page size or page index changes).
 * @param {boolean} [showSelectedRowContent=true] - Whether to show the content related to selected rows (defaults to true).
 * @param {number} [totalCount] - Total number of records across all pages; shows "Showing x–y of total" when the selection text is hidden.
 */

const PAGE_SIZE_OPTIONS = [10, 15, 20, 30, 50];

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  onPaginationChange?: (pagination: { pageIndex: number; pageSize: number }) => void;
  showSelectedRowContent?: boolean;
  totalCount?: number;
}

export function DataTablePagination<TData>({
  table,
  onPaginationChange,
  showSelectedRowContent = true,
  totalCount,
}: Readonly<DataTablePaginationProps<TData>>) {
  const { t } = useTranslation();
  const currentPageSize = table.getState().pagination.pageSize;
  // Always offer the active size: a table opened at 15 rows would otherwise render an empty select.
  const pageSizes = Array.from(new Set([...PAGE_SIZE_OPTIONS, currentPageSize])).sort(
    (a, b) => a - b
  );
  const { pageIndex } = table.getState().pagination;
  const rangeStart = totalCount ? pageIndex * currentPageSize + 1 : 0;
  const rangeEnd = Math.min((pageIndex + 1) * currentPageSize, totalCount ?? 0);

  return (
    <div className="flex w-full flex-wrap items-center justify-between gap-2 px-2">
      {showSelectedRowContent ? (
        <div className="text-sm text-muted-foreground">
          {table.getFilteredSelectedRowModel().rows.length} {t('DATA_TABLE_OF')}{' '}
          {table.getFilteredRowModel().rows.length} {t('ROWS_SELECTED')}
        </div>
      ) : null}
      {!showSelectedRowContent && totalCount !== undefined ? (
        <div className="whitespace-nowrap text-sm text-muted-foreground">
          {t('DATA_TABLE_SHOWING_RANGE', { from: rangeStart, to: rangeEnd, total: totalCount })}
        </div>
      ) : null}
      <div
        className={cn(
          'flex flex-wrap items-center gap-x-4 gap-y-2',
          !showSelectedRowContent && 'ml-auto justify-end'
        )}
      >
        <div className="flex items-center space-x-2">
          <p className="hidden whitespace-nowrap text-sm font-medium sm:flex">
            {t('ROWS_PER_PAGE')}
          </p>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              const newSize = Number(value);
              table.setPageSize(newSize);
              onPaginationChange?.({
                pageSize: newSize,
                pageIndex: 0,
              });
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {pageSizes.map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex min-w-[100px] items-center justify-center whitespace-nowrap text-sm font-medium">
          {t('PAGE')} {table.getState().pagination.pageIndex + 1} {t('DATA_TABLE_OF')}{' '}
          {table.getPageCount()}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => {
              table.setPageIndex(0);
              onPaginationChange?.({
                pageIndex: 0,
                pageSize: table.getState().pagination.pageSize,
              });
            }}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">{t('GO_TO_FIRST_PAGE')}</span>
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => {
              const previousPage = table.getState().pagination.pageIndex - 1;
              table.previousPage();
              onPaginationChange?.({
                pageIndex: previousPage,
                pageSize: table.getState().pagination.pageSize,
              });
            }}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">{t('GO_TO_PREVIOUS_PAGE')}</span>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => {
              const nextPage = table.getState().pagination.pageIndex + 1;
              table.nextPage();
              onPaginationChange?.({
                pageIndex: nextPage,
                pageSize: table.getState().pagination.pageSize,
              });
            }}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">{t('GO_TO_NEXT_PAGE')}</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => {
              const lastPage = table.getPageCount() - 1;
              table.setPageIndex(lastPage);
              onPaginationChange?.({
                pageIndex: lastPage,
                pageSize: table.getState().pagination.pageSize,
              });
            }}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">{t('GO_TO_LAST_PAGE')}</span>
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
